import { of, switchMap, filter, Subject, merge, BehaviorSubject } from "rxjs";
import {
  catchError,
  throttleTime,
  map,
  tap,
  mergeMap,
  takeUntil,
} from "rxjs/operators";
import clamp from "../../../../common/helpers/clamp";
import flatten from "../../helpers/flatten";
import TrackerArea from "../../../../models/TrackerArea";

function dataLoader(options = {}) {
  const destroy$ = new Subject();
  const paddingFactor = options?.paddingFactor || 1;
  const maxRange = options?.maxRange || 2200000;
  const delay = options?.delay || 1200;
  const apiFn = options?.apiFn || of(null);

  const isValidRange$ = new Subject();
  const regionsUpdate$ = new BehaviorSubject({});
  const position$ = new Subject();

  const successTracker = new TrackerArea(); // loaded regions
  const errorTracker = new TrackerArea(0, 0, true); // error regions
  const loadingTracker = new TrackerArea(); // loading regions
  const unloadedTracker = new TrackerArea(-1000000000, 4000000000); // not loaded yet regions

  function onLoadStart(params) {
    loadingTracker.addArea(params.g0, params.g1);
    notifyUpdate();
  }

  function onLoadEnd(params) {
    loadingTracker.removeArea(params.g0, params.g1);
    notifyUpdate();
  }

  function errorHandler(params) {
    return (e) => {
      errorTracker.addArea(params.g0, params.g1, e);
      console.error(e);
      notifyUpdate();
      return of(null);
    };
  }

  function notifyUpdate() {
    regionsUpdate$.next({
      loadingTracker,
      errorTracker,
      unloadedTracker,
      successTracker,
    });
  }

  function isValidRangeSize({ size }) {
    return size > 1 && size < maxRange;
  }

  function checkRanges(ranges) {
    const total = ranges.reduce((a, x) => a + x.size, 0);
    const isValidRange = isValidRangeSize({ size: total });
    isValidRange$.next(isValidRange);
    return isValidRange ? ranges.filter(isValidRangeSize) : [];
  }

  function getPadded({ g0, g1, chr }) {
    const len = g1 - g0;
    const expanded = len * paddingFactor;
    const remaining = Math.max(0, maxRange - len);
    const padding = clamp(expanded, 0, remaining);
    const s = Math.floor(g0 - padding / 2);
    const e = Math.ceil(g1 + padding / 2);
    const start = clamp(s, chr.region.g0, chr.region.g1) + 1;
    const end = clamp(e, chr.region.g0, chr.region.g1);
    return [start, end];
  }

  function getRegionForRange(start, end, chr) {
    return {
      g0: start,
      g1: end,
      chr,
      chrId: chr.id,
      genomeId: chr.genomeId,
      x0: start - chr.region.g0,
      x1: end - chr.region.g0,
      size: end - start,
    };
  }

  function checkNoNovel({ g0, g1 }) {
    return successTracker.findNovelAreas([g0, g1]).length === 0;
  }

  function getNovelForRange(range) {
    return checkNoNovel(range)
      ? []
      : successTracker
          .findNovelAreas(getPadded(range))
          .map(([start, end]) => getRegionForRange(start, end, range.chr))
          .filter((x) => x.size > 0);
  }

  function getNovelAreas(ranges) {
    return flatten(ranges.map(getNovelForRange));
  }

  function handleSuccess(params) {
    unloadedTracker.removeArea(params.g0, params.g1);
    errorTracker.removeArea(params.g0, params.g1);
  }

  function reqForArea(params, reqData) {
    return of(null).pipe(
      tap(() => onLoadStart(params)),
      tap(() => successTracker.addArea(params.g0, params.g1)),
      switchMap(() => apiFn({ ...params, ...reqData })),
      takeUntil(destroy$),
      map((res) => ({ ...params, res })),
      tap(() => handleSuccess(params)),
      catchError(errorHandler(params)),
      tap(() => onLoadEnd(params))
    );
  }

  function calcRequests({ ranges, reqParams }) {
    const validRanges = checkRanges(ranges);
    const areas = getNovelAreas(validRanges);
    return areas.map((x) => reqForArea(x, reqParams));
  }

  return {
    destroy: () => {
      successTracker.reset();
      errorTracker.reset();
      unloadedTracker.reset();
      loadingTracker.reset();
      destroy$.next();
    },
    setPosition: (ranges, reqParams) => {
      position$.next({ ranges, reqParams });
    },
    isValidRange$,
    regionsUpdate$,
    response$: position$.pipe(
      throttleTime(delay, undefined, { leading: true, trailing: true }),
      filter((req) => req?.ranges?.length),
      map(calcRequests),
      mergeMap((reqs) => merge(...reqs)),
      filter(Boolean),
      takeUntil(destroy$)
    ),
  };
}

export default dataLoader;
